import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSocket, initiateSocket } from '../socket';
import { AuthContext } from '../context/AuthContext';
import axiosInstance from '../axiosInstance';
import './NotificationBell.css';
import { FiCalendar, FiClock, FiThermometer } from 'react-icons/fi';
import useHrMe from '../hr/useHrMe';
import { hr } from '../hr/api';

// HR bell alerts (spec 16.5 B), for HR people only (owner emails + the HR access
// list). The server decides who is HR: it serves the items and fills the socket
// room `hr`; useHrMe's isHr only decides whether to ask.
const HR_EVENTS = ['hr:sick-reported', 'hr:request-waiting'];
const HR_ICONS = { 'out-today': FiCalendar, 'request-waiting': FiClock, 'sick-reported': FiThermometer };
const HR_ICON_STYLE = { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' };

// HR items cleared in this tab, for the signed-in account only. Kept in memory
// (no storage): a reload shows again whatever is still current.
const hrHidden = { user: null, ids: new Set() };
function hiddenHrIds(userKey) {
  if (hrHidden.user !== userKey) {
    hrHidden.user = userKey;
    hrHidden.ids = new Set();
  }
  return hrHidden.ids;
}

// A well-formed HR item, or null. Links only ever lead into the HR Portal.
function toHrItem(data) {
  if (!data || typeof data.id !== 'string' || typeof data.text !== 'string' || !HR_ICONS[data.kind]) return null;
  const inPortal = typeof data.link === 'string' && /^\/hr-portal(?:[/?#]|$)/.test(data.link) && !data.link.includes('..');
  return { id: data.id, kind: data.kind, text: data.text, link: inPortal ? data.link : '/hr-portal', at: data.at || null, employeeId: data.employeeId || null };
}

function NotificationBell() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const dropdownRef = useRef(null);
  const isSuperAdmin = user?.isSuperAdmin;
  // HR alerts: only for people the HR API says are HR (never because of super admin).
  const hrMe = useHrMe({ enabled: !!user && user.role !== 'pending' });
  const isHr = hrMe.me?.isHr === true;
  const myEmployeeId = hrMe.me?.employee?.id || null;
  const hrUserKey = user?._id || user?.id || user?.email || null;
  const [hrItems, setHrItems] = useState([]);

  // Fetch pending users on mount (only for super admins)
  useEffect(() => {
    if (!isSuperAdmin) return;

    const fetchPendingUsers = async () => {
      try {
        const res = await axiosInstance.get('/audit/pending-users');
        if (res.data?.pendingUsers) {
          setNotifications(res.data.pendingUsers.map(u => ({
            id: u._id,
            type: 'pending_user',
            message: `${u.email} is awaiting approval`,
            email: u.email,
            // Records predating the accountType field are employees.
            accountType: u.accountType || 'employee',
            name: u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email,
            timestamp: u.createdAt
          })));
        }
      } catch (err) {
        console.error('Failed to fetch pending users:', err);
      }
    };

    fetchPendingUsers();
  }, [isSuperAdmin]);

  // Listen for real-time notifications via WebSocket
  useEffect(() => {
    if (!isSuperAdmin) return;

    initiateSocket();          // ensure the shared socket exists (bell mounts before any page inits it)
    const socket = getSocket();
    if (!socket) return;

    const handleNewPendingUser = (data) => {
      setNotifications(prev => {
        // Avoid duplicates
        if (prev.some(n => n.id === data.userId)) return prev;
        return [{
          id: data.userId,
          type: 'pending_user',
          message: `${data.email} is awaiting approval`,
          email: data.email,
          // Only the internal signup path emits this today, hence the default.
          accountType: data.accountType || 'employee',
          name: data.name || data.email,
          timestamp: new Date().toISOString()
        }, ...prev];
      });
    };

    const handleUserApproved = (data) => {
      // Remove from notifications when user is approved
      setNotifications(prev => prev.filter(n => n.id !== data.userId));
    };

    socket.on('notification:pending-user', handleNewPendingUser);
    socket.on('notification:user-approved', handleUserApproved);

    return () => {
      socket.off('notification:pending-user', handleNewPendingUser);
      socket.off('notification:user-approved', handleUserApproved);
    };
  }, [isSuperAdmin]);

  // HR: the on-load items (out today, requests waiting on HR, recent sick
  // reports). A failed answer just means no HR items; it never blocks the bell.
  useEffect(() => {
    if (!isHr) {
      setHrItems(prev => (prev.length ? [] : prev));
      return undefined;
    }
    let active = true;
    hr.admin.notifications()
      .then((data) => {
        if (!active) return;
        const hidden = hiddenHrIds(hrUserKey);
        const loaded = (Array.isArray(data?.items) ? data.items : []).map(toHrItem).filter(item => item && !hidden.has(item.id));
        setHrItems(prev => [...prev, ...loaded.filter(item => !prev.some(p => p.id === item.id))]);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [isHr, hrUserKey]);

  // HR: live alerts from the server-checked socket room `hr`.
  useEffect(() => {
    if (!isHr) return undefined;

    initiateSocket();
    const socket = getSocket();
    if (!socket) return undefined;

    const handleHrEvent = (data) => {
      const item = toHrItem(data);
      if (!item || hiddenHrIds(hrUserKey).has(item.id)) return;
      if (myEmployeeId && item.employeeId === myEmployeeId) return; // their own request or sick day
      setHrItems(prev => (prev.some(p => p.id === item.id) ? prev : [item, ...prev]));
    };

    HR_EVENTS.forEach(event => socket.on(event, handleHrEvent));
    return () => HR_EVENTS.forEach(event => socket.off(event, handleHrEvent));
  }, [isHr, myEmployeeId, hrUserKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification) => {
    setIsOpen(false);
    // Clicking a notification dismisses it from the bell. If the account is still
    // pending, the mount-time fetch of /audit/pending-users will resurface it on
    // next load; once actually approved, notification:user-approved keeps it gone.
    setNotifications(prev => prev.filter(n => n.id !== notification.id));
    if (notification.type === 'pending_user') {
      // Land on the surface that can actually approve this account — customers
      // and employees are approved on different tabs, and User Management opens
      // on Employees by default.
      const surface = notification.accountType === 'customer' ? 'customers' : 'employees';
      navigate(`/audit-logs?tab=users&surface=${surface}`);
    }
  };

  const clearNotification = (e, id) => {
    e.stopPropagation();
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const hideHrItem = (id) => {
    hiddenHrIds(hrUserKey).add(id);
    setHrItems(prev => prev.filter(item => item.id !== id));
  };

  const handleHrItemClick = (item) => {
    setIsOpen(false);
    hideHrItem(item.id);
    navigate(item.link);
  };

  const clearHrItem = (e, id) => {
    e.stopPropagation();
    hideHrItem(id);
  };

  const clearAll = () => {
    setNotifications([]);
    const hidden = hiddenHrIds(hrUserKey);
    hrItems.forEach(item => hidden.add(item.id));
    setHrItems([]);
  };

  const totalCount = notifications.length + hrItems.length;

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  // Only render for super admins (pending-user alerts) and HR people (HR alerts)
  if (!isSuperAdmin && !isHr) return null;

  return (
    <div className="notification-bell" ref={dropdownRef}>
      <button
        className="bell-button"
        onClick={() => setIsOpen(!isOpen)}
        title="Notifications"
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/>
        </svg>
        {totalCount > 0 && (
          <span className="notification-badge">{totalCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-header">
            <span>Notifications</span>
            {totalCount > 0 && (
              <button
                className="clear-all"
                onClick={clearAll}
              >
                Clear all
              </button>
            )}
          </div>

          <div className="notification-list">
            {hrItems.map(item => {
              const Icon = HR_ICONS[item.kind];
              return (
                <div
                  key={item.id}
                  className="notification-item hrp-bell-item"
                  onClick={() => handleHrItemClick(item)}
                >
                  <div className="notification-icon hrp-bell-icon" style={HR_ICON_STYLE}>
                    <Icon size={16} aria-hidden="true" />
                  </div>
                  <div className="notification-content">
                    <div className="notification-message">{item.text}</div>
                    <div className="notification-time">{item.kind === 'out-today' ? 'Today' : (item.at ? formatTime(item.at) : '')}</div>
                  </div>
                  <button
                    className="dismiss-btn"
                    onClick={(e) => clearHrItem(e, item.id)}
                    title="Dismiss"
                  >
                    &times;
                  </button>
                </div>
              );
            })}
            {totalCount === 0 ? (
              <div className="no-notifications">No new notifications</div>
            ) : (
              notifications.map(notification => (
                <div
                  key={notification.id}
                  className="notification-item"
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon pending-user">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </div>
                  <div className="notification-content">
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-time">{formatTime(notification.timestamp)}</div>
                  </div>
                  <button
                    className="dismiss-btn"
                    onClick={(e) => clearNotification(e, notification.id)}
                    title="Dismiss"
                  >
                    &times;
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
